import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { createElement } from "@wordpress/element";

/** @format */

/**
 * External dependencies
 */
import { Button } from '@wordpress/components';
import classnames from 'classnames';
import { Component, createRef } from '@wordpress/element';
import { isEqual } from 'lodash';
import { ENTER, ESCAPE, UP, DOWN, LEFT, RIGHT } from '@wordpress/keycodes';
import PropTypes from 'prop-types';
/**
 * A list box that displays filtered options after search.
 */

var List =
/*#__PURE__*/
function (_Component) {
  _inherits(List, _Component);

  function List() {
    var _this;

    _classCallCheck(this, List);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(List).apply(this, arguments));
    _this.handleKeyDown = _this.handleKeyDown.bind(_assertThisInitialized(_this));
    _this.select = _this.select.bind(_assertThisInitialized(_this));
    _this.optionRefs = {};
    _this.listbox = createRef();
    return _this;
  }

  _createClass(List, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props = this.props,
          options = _this$props.options,
          selectedIndex = _this$props.selectedIndex; // Remove old option refs to avoid memory leaks.

      if (!isEqual(options, prevProps.options)) {
        this.optionRefs = {};
      }

      if (selectedIndex !== prevProps.selectedIndex) {
        this.scrollToOption(selectedIndex);
      }
    }
  }, {
    key: "getOptionRef",
    value: function getOptionRef(index) {
      if (!this.optionRefs.hasOwnProperty(index)) {
        this.optionRefs[index] = createRef();
      }

      return this.optionRefs[index];
    }
  }, {
    key: "select",
    value: function select(option) {
      var onSelect = this.props.onSelect;

      if (option.isDisabled) {
        return;
      }

      onSelect(option);
    }
  }, {
    key: "scrollToOption",
    value: function scrollToOption(index) {
      var listbox = this.listbox.current;

      if (listbox.scrollHeight <= listbox.clientHeight) {
        return;
      }

      if (!this.optionRefs[index]) {
        return;
      }

      var option = this.optionRefs[index].current;
      var scrollBottom = listbox.clientHeight + listbox.scrollTop;
      var elementBottom = option.offsetTop + option.offsetHeight;

      if (elementBottom > scrollBottom) {
        listbox.scrollTop = elementBottom - listbox.clientHeight;
      } else if (option.offsetTop < listbox.scrollTop) {
        listbox.scrollTop = option.offsetTop;
      }
    }
  }, {
    key: "handleKeyDown",
    value: function handleKeyDown(event) {
      var _this$props2 = this.props,
          decrementSelectedIndex = _this$props2.decrementSelectedIndex,
          incrementSelectedIndex = _this$props2.incrementSelectedIndex,
          options = _this$props2.options,
          onSearch = _this$props2.onSearch,
          selectedIndex = _this$props2.selectedIndex,
          setExpanded = _this$props2.setExpanded;

      if (options.length === 0) {
        return;
      }

      switch (event.keyCode) {
        case UP:
          decrementSelectedIndex();
          event.preventDefault();
          event.stopPropagation();
          break;

        case DOWN:
          incrementSelectedIndex();
          event.preventDefault();
          event.stopPropagation();
          break;

        case ENTER:
          this.select(options[selectedIndex]);
          event.preventDefault();
          event.stopPropagation();
          break;

        case LEFT:
        case RIGHT:
          setExpanded(false);
          break;

        case ESCAPE:
          setExpanded(false);
          onSearch(null);
          return;

        default:
          return;
      }
    }
  }, {
    key: "toggleKeyEvents",
    value: function toggleKeyEvents(isListening) {
      var node = this.props.node; // This exists because we must capture ENTER key presses before RichText.
      // It seems that react fires the simulated capturing events after the
      // native browser event has already bubbled so we can't stopPropagation
      // and avoid RichText getting the event from TinyMCE, hence we must
      // register a native event handler.

      var handler = isListening ? 'addEventListener' : 'removeEventListener';
      node[handler]('keydown', this.handleKeyDown, true);
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.toggleKeyEvents(true);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.toggleKeyEvents(false);
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props3 = this.props,
          instanceId = _this$props3.instanceId,
          listboxId = _this$props3.listboxId,
          options = _this$props3.options,
          selectedIndex = _this$props3.selectedIndex,
          staticList = _this$props3.staticList;
      var listboxClasses = classnames('woocommerce-select-control__listbox', {
        'is-static': staticList
      });
      return createElement("div", {
        ref: this.listbox,
        id: listboxId,
        role: "listbox",
        className: listboxClasses
      }, options.map(function (option, index) {
        return createElement(Button, {
          ref: _this2.getOptionRef(index),
          key: option.key,
          id: "woocommerce-select-control__option-".concat(instanceId, "-").concat(option.key),
          role: "option",
          "aria-selected": index === selectedIndex,
          disabled: option.isDisabled,
          className: classnames('woocommerce-select-control__option', {
            'is-selected': index === selectedIndex
          }),
          onClick: function onClick() {
            return _this2.select(option);
          },
          tabIndex: "-1"
        }, option.label);
      }));
    }
  }]);

  return List;
}(Component);

List.propTypes = {
  /**
   * ID of the main SelectControl instance.
   */
  instanceId: PropTypes.number,

  /**
   * ID used for a11y in the listbox.
   */
  listboxId: PropTypes.string,

  /**
   * Parent node to bind keyboard events to.
   */
  node: PropTypes.instanceOf(Element).isRequired,

  /**
   * Function to execute when an option is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Array of options to display.
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    isDisabled: PropTypes.bool,
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    keywords: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    value: PropTypes.any
  })).isRequired,

  /**
   * Integer for the currently selected item.
   */
  selectedIndex: PropTypes.number,

  /**
   * Bool to determine if the list should be positioned absolutely or staticly.
   */
  staticList: PropTypes.bool
};
export default List;
//# sourceMappingURL=list.js.map